import React, { useState, useEffect } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Brain, User, Phone, Calendar, ArrowRight, CheckCircle, TrendingUp, Users, Award, Sparkles, MapPin, Globe, Flag } from 'lucide-react';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

// List of Indian states
const indianStates = [
  'Andaman and Nicobar Islands', 'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar',
  'Chandigarh', 'Chhattisgarh', 'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Goa',
  'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jammu and Kashmir', 'Jharkhand', 'Karnataka',
  'Kerala', 'Ladakh', 'Lakshadweep', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Puducherry', 'Punjab', 'Rajasthan',
  'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
];

const Psychology = () => {
  const questions = [
    // Critical Thinking (5 questions)
    {
      text: "If you found a mysterious map in your backyard, what would you do first to figure out where it leads?",
      options: [
        "Look for landmarks or clues around where I found it.",
        "Draw my own map to compare it with the mysterious one.",
        "Ask a friend to help me guess what it means.",
        "Hide it and check it later when I have more time."
      ],
      category: "Critical Thinking"
    },
    {
      text: "Imagine you’re building a toy rocket, but you’re missing one piece. How would you solve this problem?",
      options: [
        "Use something else that’s close enough to the missing piece.",
        "Start over with a different design that doesn’t need that piece.",
        "Look up instructions online to see what others did.",
        "Wait until I find the exact piece I need."
      ],
      category: "Critical Thinking"
    },
    {
      text: "If you could create a new rule for your school, how would you decide what rule to make?",
      options: [
        "Think about what would make school more fun for everyone.",
        "Ask my teachers what they think the school needs.",
        "Make a rule that helps me and my friends the most.",
        "Choose something I saw in a movie or book."
      ],
      category: "Critical Thinking"
    },
    {
      text: "You’re playing a game with friends, and someone suggests a new way to play. How do you decide if it’s a good idea?",
      options: [
        "Try it once to see if it makes the game better.",
        "Talk about it with everyone to hear their thoughts.",
        "Stick to the old rules because they’re already fun.",
        "Make up my own new rule instead."
      ],
      category: "Critical Thinking"
    },
    {
      text: "If you could invent a new toy, how would you figure out what kids would like about it?",
      options: [
        "Ask my friends what they like to play with.",
        "Make a toy I think is cool and test it out.",
        "Look at other toys in stores to get ideas.",
        "Imagine what I wanted when I was younger."
      ],
      category: "Critical Thinking"
    },
    // Situational Thinking (5 questions)
    {
      text: "Your friend is upset because they lost a game. What do you do to help them feel better?",
      options: [
        "Suggest playing a different game together.",
        "Tell them a funny story to make them laugh.",
        "Say it’s okay and talk about what they did well.",
        "Let them be alone for a while to calm down."
      ],
      category: "Situational Thinking"
    },
    {
      text: "You’re in a group project, and one person isn’t helping. What would you do?",
      options: [
        "Talk to them to find out why they’re not helping.",
        "Do their part myself to finish the project faster.",
        "Tell the teacher about it right away.",
        "Wait and see if they start helping later."
      ],
      category: "Situational Thinking"
    },
    {
      text: "You’re at a new park and get lost. What’s the first thing you do?",
      options: [
        "Look for a map or sign to find my way.",
        "Ask someone nearby for directions.",
        "Stay where I am and call for help.",
        "Try to remember the way I came and go back."
      ],
      category: "Situational Thinking"
    },
    {
      text: "You accidentally break something that belongs to your sibling. How do you handle it?",
      options: [
        "Tell them right away and say I’m sorry.",
        "Try to fix it before they notice.",
        "Explain it was an accident and offer to share something of mine.",
        "Wait until they find out and see what they say."
      ],
      category: "Situational Thinking"
    },
    {
      text: "You’re invited to two different birthday parties at the same time. How do you choose which one to go to?",
      options: [
        "Go to the one with the friend I know better.",
        "Pick the party that sounds more fun.",
        "Talk to both friends to see if I can go to both.",
        "Choose the one that’s closer to my house."
      ],
      category: "Situational Thinking"
    },
    // Analytical Reasoning (5 questions)
    {
      text: "You’re given a puzzle with pieces that don’t seem to fit together. How do you figure it out?",
      options: [
        "Sort the pieces by color or shape to find patterns.",
        "Try fitting random pieces together to see what works.",
        "Look at the picture on the box for clues.",
        "Ask someone else to help me start it."
      ],
      category: "Analytical Reasoning"
    },
    {
      text: "You notice your pet is acting differently. How do you figure out what’s wrong?",
      options: [
        "Watch them closely to see what they’re doing.",
        "Think about what changed recently, like their food.",
        "Ask an adult to help me check on them.",
        "Give them extra attention to see if they get better."
      ],
      category: "Analytical Reasoning"
    },
    {
      text: "You’re saving up for a new toy, but it’s expensive. How do you plan to get enough money?",
      options: [
        "Make a list of chores I can do to earn money.",
        "Save all my allowance until I have enough.",
        "Ask for the toy as a gift for my birthday.",
        "Look for a cheaper toy that’s just as good."
      ],
      category: "Analytical Reasoning"
    },
    {
      text: "You’re trying to learn a new game with tricky rules. How do you get better at it?",
      options: [
        "Practice a little bit every day to get used to it.",
        "Watch someone who’s good at the game to learn their tricks.",
        "Read the rules again to make sure I understand them.",
        "Play with easier rules until I’m ready for the hard ones."
      ],
      category: "Analytical Reasoning"
    },
    {
      text: "You’re building a tower with blocks, but it keeps falling. How do you figure out why?",
      options: [
        "Check if the ground is flat and even.",
        "Try using different blocks to see if they work better.",
        "Build a smaller tower to test what’s wrong.",
        "Keep building the same way until it stays up."
      ],
      category: "Analytical Reasoning"
    }
  ];

  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState(Array(questions.length).fill(undefined));
  const [parentData, setParentData] = useState({
    whatsappNumber: '',
    childName: '',
    childAge: 5,
    childState: '',
  });
  const [currentSection, setCurrentSection] = useState('parentInfo');
  const [scores, setScores] = useState({
    critical: 0,
    situational: 0,
    analytical: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [animationPhase, setAnimationPhase] = useState('idle');

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationPhase(prev => (prev === 'idle' ? 'float' : 'idle'));
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setParentData({ ...parentData, [id]: value });
  };

  const handleAgeChange = (e) => {
    setParentData({ ...parentData, childAge: parseInt(e.target.value) });
  };

  const startAssessment = () => {
    const { whatsappNumber, childName, childAge, childState } = parentData;
    if (!whatsappNumber || !childName || childAge < 5 || !childState) {
      alert('Please fill in all required fields, including state.');
      return;
    }
    setCurrentSection('questionsSection');
  };

  const displayQuestion = () => {
    return (
      <div className="question-container">
        <div className="question-header">
          <div className="question-number">{currentQuestion + 1}</div>
          <div className="question-category">{questions[currentQuestion].category}</div>
        </div>
        <h3 className="question-text">{questions[currentQuestion].text}</h3>
        <div className="options-grid">
          {questions[currentQuestion].options.map((option, index) => (
            <label
              key={index}
              className={`option-card ${answers[currentQuestion] === index ? 'selected' : ''}`}
              onClick={() => {
                const newAnswers = [...answers];
                newAnswers[currentQuestion] = index;
                setAnswers(newAnswers);
              }}
            >
              <input
                type="radio"
                name={`q${currentQuestion}`}
                value={index}
                checked={answers[currentQuestion] === index}
                onChange={() => {}}
                style={{ display: 'none' }}
              />
              <div className="option-content">
                <div className="option-check">
                  {answers[currentQuestion] === index && <CheckCircle size={20} />}
                </div>
                <span className="option-text">{option}</span>
              </div>
            </label>
          ))}
        </div>
      </div>
    );
  };

  const nextQuestion = () => {
    if (answers[currentQuestion] === undefined) {
      alert('Please select an answer.');
      return;
    }
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const calculateResult = async () => {
    if (answers[currentQuestion] === undefined) {
      alert('Please select an answer.');
      return;
    }

    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 2000));

    const categoryScores = {
      "Critical Thinking": 0,
      "Situational Thinking": 0,
      "Analytical Reasoning": 0,
    };

    answers.forEach((answer, index) => {
      const category = questions[index].category;
      categoryScores[category] += answer;
    });

    const maxCategoryScore = 15; // 5 questions * 3 points max per question
    const percentages = {
      critical: Math.min(Math.round((categoryScores["Critical Thinking"] / maxCategoryScore) * 80), 80),
      situational: Math.min(Math.round((categoryScores["Situational Thinking"] / maxCategoryScore) * 80), 80),
      analytical: Math.min(Math.round((categoryScores["Analytical Reasoning"] / maxCategoryScore) * 80), 80),
    };

    setScores(percentages);
    setIsLoading(false);
    setCurrentSection('resultsSection');

    console.log('Assessment data:', {
      ...parentData,
      scores: percentages,
      answers,
      timestamp: new Date().toISOString(),
    });
  };

  const restartAssessment = () => {
    setCurrentQuestion(0);
    setAnswers(Array(questions.length).fill(undefined));
    setParentData({
      whatsappNumber: '',
      childName: '',
      childAge: 5,
      childState: '',
    });
    setScores({ critical: 0, situational: 0, analytical: 0 });
    setCurrentSection('parentInfo');
  };

  const createChartData = (score, color) => ({
    labels: ['Score', 'Remaining'],
    datasets: [
      {
        data: [score, 80 - score],
        backgroundColor: [color, 'rgba(255, 255, 255, 0.1)'],
        borderColor: ['transparent', 'transparent'],
        cutout: '80%',
      },
    ],
  });

  const chartOptions = {
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    maintainAspectRatio: false,
  };

  const overallScore = Math.round((scores.critical + scores.situational + scores.analytical) / 3);

  const getPercentiles = () => {
    const basePercentile = Math.min(99, Math.round(overallScore * 1.2));
    return {
      world: Math.max(basePercentile - 5, 0),
      country: basePercentile,
      state: Math.min(basePercentile + 5, 99),
    };
  };

  const getTopPercent = (percentile) => {
    return Math.max(1, 100 - percentile);
  };

  return (
    <div className="app-container">
      <div className="main-container">
        {/* Parent Information Section */}
        {currentSection === 'parentInfo' && (
          <div className="section-container fade-in">
            <div className="card glass-card">
              <div className="card-header">
                <div className="header-badge">
                  <User size={20} />
                  <span>Parent Information</span>
                </div>
                <h2>Let's get started</h2>
                <p>We'll send a comprehensive report to your WhatsApp</p>
              </div>

              <div className="form-container">
                <div className="input-group">
                  <label htmlFor="whatsappNumber">
                    <Phone size={18} />
                    WhatsApp Number
                  </label>
                  <input
                    type="tel"
                    id="whatsappNumber"
                    placeholder="+91"
                    value={parentData.whatsappNumber}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="input-group">
                  <label htmlFor="childName">
                    <Sparkles size={18} />
                    Child's Name
                  </label>
                  <input
                    type="text"
                    id="childName"
                    value={parentData.childName}
                    onChange={handleInputChange}
                    placeholder="Enter child's name"
                    required
                  />
                </div>

                <div className="input-group">
                  <label htmlFor="childState">
                    <MapPin size={18} />
                    Child's State
                  </label>
                  <select
                    id="childState"
                    value={parentData.childState}
                    onChange={handleInputChange}
                    className="modern-select"
                    required
                  >
                    <option value="" disabled>
                      Select a state
                    </option>
                    {indianStates.map((state, index) => (
                      <option key={index} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="input-group">
                  <label htmlFor="childAge">
                    <Calendar size={18} />
                    Child's Age: {parentData.childAge} years
                  </label>
                  <div className="slider-container">
                    <input
                      type="range"
                      id="childAge"
                      min="5"
                      max="18"
                      value={parentData.childAge}
                      onChange={handleAgeChange}
                      className="modern-slider"
                    />
                    <div className="slider-track">
                      <div
                        className="slider-progress"
                        style={{ width: `${((parentData.childAge - 5) / 13) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>

                <button className="primary-button" onClick={startAssessment}>
                  <span>Begin Assessment</span>
                  <ArrowRight size={20} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Questions Section */}
        {currentSection === 'questionsSection' && (
          <div className="section-container fade-in">
            <div className="progress-container">
              <div className="progress-header">
                <span className="progress-text">Question {currentQuestion + 1} of {questions.length}</span>
                <span className="progress-percentage">{Math.round(((currentQuestion + 1) / questions.length) * 100)}%</span>
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
                ></div>
              </div>
            </div>

            <div className="card glass-card question-card">{displayQuestion()}</div>

            <div className="navigation-buttons">
              <div style={{ flex: 1 }}></div>
              {currentQuestion < questions.length - 1 ? (
                <button
                  className="primary-button"
                  onClick={nextQuestion}
                  disabled={answers[currentQuestion] === undefined}
                >
                  <span>Next</span>
                  <ArrowRight size={20} />
                </button>
              ) : (
                <button
                  className="primary-button complete-button"
                  onClick={calculateResult}
                  disabled={answers[currentQuestion] === undefined}
                >
                  <span>Complete Assessment</span>
                  <CheckCircle size={20} />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Loading Section */}
        {isLoading && (
          <div className="section-container fade-in">
            <div className="loading-container">
              <div className="loading-animation">
                <div className="loading-brain">
                  <Brain size={64} />
                </div>
                <div className="loading-circles">
                  <div className="circle circle-1"></div>
                  <div className="circle circle-2"></div>
                  <div className="circle circle-3"></div>
                </div>
              </div>
              <h2>Analyzing Results</h2>
            </div>
          </div>
        )}

        {/* Results Section */}
        {currentSection === 'resultsSection' && (
          <div className="section-container fade-in">
            <div className="results-container">
              <div className="results-header">
                <div className="results-badge">
                  <Award size={24} />
                  <span>Assessment Complete</span>
                </div>
              </div>

              <div className="score-showcase">
                <div className="overall-score">
                  <div className="score-circle">
                    <div className="score-value">{overallScore}</div>
                    <div className="score-label">Relevancy Score</div>
                  </div>
                  <div className="score-gradient"></div>
                </div>
              </div>

              <div className="metrics-grid">
                <div className="metric-card critical">
                  <div className="metric-header">
                    <Brain size={24} />
                    <span>Critical Thinking</span>
                  </div>
                  <div className="metric-chart">
                    <Doughnut
                      data={createChartData(scores.critical, 'rgb(4, 0, 255)')}
                      options={chartOptions}
                    />
                    <div className="chart-overlay">
                      <span className="chart-score">{scores.critical}%</span>
                    </div>
                  </div>
                </div>

                <div className="metric-card situational">
                  <div className="metric-header">
                    <TrendingUp size={24} />
                    <span>Situational Thinking</span>
                  </div>
                  <div className="metric-chart">
                    <Doughnut
                      data={createChartData(scores.situational, 'rgb(4, 0, 255)')}
                      options={chartOptions}
                    />
                    <div className="chart-overlay">
                      <span className="chart-score">{scores.situational}%</span>
                    </div>
                  </div>
                </div>

                <div className="metric-card analytical">
                  <div className="metric-header">
                    <Sparkles size={24} />
                    <span>Analytical Reasoning</span>
                  </div>
                  <div className="metric-chart">
                    <Doughnut
                      data={createChartData(scores.analytical, 'rgb(4, 0, 255)')}
                      options={chartOptions}
                    />
                    <div className="chart-overlay">
                      <span className="chart-score">{scores.analytical}%</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="comparison-section">
                <h3 className="comparison-title">Comparison with other Childrens of same Age-Group</h3>
                <div className="comparison-grid">
                  <div className="comparison-card world" style={{ animationDelay: '0s' }}>
                    <div className="comparison-header">
                      <Globe size={20} />
                      <span>World</span>
                    </div>
                    <div className="comparison-body">
                      <p>{100 - getPercentiles().world}% of peers performed better</p>
                      <div className="top-badge">Top {getTopPercent(getPercentiles().world)}%</div>
                    </div>
                  </div>
                  <div className="comparison-card country" style={{ animationDelay: '0.2s' }}>
                    <div className="comparison-header">
                      <Flag size={20} />
                      <span>India</span>
                    </div>
                    <div className="comparison-body">
                      <p>{100 - getPercentiles().country}% of peers performed better</p>
                      <div className="top-badge">Top {getTopPercent(getPercentiles().country)}%</div>
                    </div>
                  </div>
                  <div className="comparison-card state" style={{ animationDelay: '0.4s' }}>
                    <div className="comparison-header">
                      <MapPin size={20} />
                      <span>{parentData.childState}</span>
                    </div>
                    <div className="comparison-body">
                      <p>{100 - getPercentiles().state}% of peers performed better</p>
                      <div className="top-badge">Top {getTopPercent(getPercentiles().state)}%</div>
                    </div>
                  </div>
                </div>
                <p className="comparison-note">Based on 10,000+ assessments</p>
              </div>

              {/* Join Program Image */}
              <div className="join-program">
                <a
                  href={`https://wa.me/+918472958581?text=${encodeURIComponent('I want to join the program')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={require('../assets/Join.jpg')}
                    alt="Join Program"
                    className="join-image"
                  />
                </a>
              </div>

              <div className="action-buttons">
                <button className="primary-button" onClick={restartAssessment}>
                  <span>Restart Assessment</span>
                  <ArrowRight size={20} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .app-container {
          min-height: 100vh;
          background: black;
          position: relative;
          overflow-x: hidden;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .main-container {
          position: relative;
          z-index: 1;
          max-width: 800px;
          margin: 0 auto;
          padding: 20px;
        }

        .section-container {
          animation: slideUp 0.6s ease-out;
        }

        .card {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 24px;
          padding: 40px;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          margin-bottom: 20px;
        }

        .glass-card {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: white;
        }

        .card-header {
          text-align: center;
          margin-bottom: 40px;
        }

        .header-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(4, 0, 255, 0.2);
          padding: 8px 16px;
          border-radius: 20px;
          color: white;
          font-size: 0.9rem;
          font-weight: 500;
          margin-bottom: 20px;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .card-header h2 {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 8px;
          color: white;
        }

        .card-header p {
          font-size: 1.1rem;
          color: rgba(255, 255, 255, 0.8);
          font-weight: 400;
        }

        .form-container {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .input-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .input-group label {
          display: flex;
          align-items: center;
          gap: 8px;
          color: white;
          font-weight: 500;
          font-size: 0.95rem;
        }

        .input-group input[type="text"],
        .input-group input[type="tel"],
        .modern-select {
          padding: 16px 20px;
          border: 2px solid rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          color: white;
          font-size: 1rem;
          font-family: 'Inter', sans-serif;
          transition: all 0.3s ease;
        }

        .input-group input[type="text"]:focus,
        .input-group input[type="tel"]:focus,
        .modern-select:focus {
          outline: none;
          border-color: rgb(4, 0, 255);
          background: rgba(255, 255, 255, 0.15);
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(4, 0, 255, 0.3);
        }

        .input-group input::placeholder {
          color: rgba(255, 255, 255, 0.6);
        }

        .modern-select {
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 20px center;
          background-size: 16px;
          font-family: 'Inter', sans-serif;
          font-size: 1rem;
          color: white;
          background-color: rgba(255, 255, 255, 0.1);
          cursor: pointer;
        }

        .modern-select option {
          font-family: 'Inter', sans-serif;
          font-size: 1rem;
          color: black;
          background: white;
          padding: 10px;
        }

        .slider-container {
          position: relative;
          margin-top: 8px;
        }

        .modern-slider {
          width: 100%;
          height: 6px;
          border-radius: 3px;
          background: transparent;
          outline: none;
          appearance: none;
          position: relative;
          z-index: 2;
        }

        .modern-slider::-webkit-slider-thumb {
          appearance: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
          border: 3px solid rgb(4, 0, 255);
          transition: all 0.2s ease;
        }

        .modern-slider::-webkit-slider-thumb:hover {
          transform: scale(1.1);
          box-shadow: 0 6px 20px rgba(4, 0, 255, 0.3);
        }

        .slider-track {
          position: absolute;
          top: 9px;
          left: 0;
          width: 100%;
          height: 6px;
          background: rgba(255, 255, 255, 0.3);
          border-radius: 3px;
          z-index: 1;
        }

        .slider-progress {
          height: 100%;
          background: rgb(4, 0, 255);
          border-radius: 3px;
          transition: width 0.3s ease;
        }

        .primary-button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 16px 32px;
          background: rgb(4, 0, 255);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(4, 0, 255, 0.4);
        }

        .primary-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(4, 0, 255, 0.5);
        }

        .primary-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .secondary-button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 16px 32px;
          background: rgba(255, 255, 255, 0.1);
          color: white;
          border: 2px solid rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
        }

        .secondary-button:hover {
          background: rgba(255, 255, 255, 0.2);
          border-color: rgb(4, 0, 255);
          transform: translateY(-2px);
        }

        .progress-container {
          margin-bottom: 30px;
        }

        .progress-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .progress-text {
          color: white;
          font-weight: 500;
        }

        .progress-percentage {
          color: rgba(255, 255, 255, 0.8);
          font-weight: 600;
        }

        .progress-bar {
          width: 100%;
          height: 8px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 4px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          background: rgb(4, 0, 255);
          border-radius: 4px;
          transition: width 0.5s ease;
          position: relative;
        }

        .progress-fill::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
          animation: shimmer 2s infinite;
        }

        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        .question-card {
          position: relative;
          overflow: hidden;
        }

        .question-container {
          margin-bottom: 30px;
        }

        .question-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 24px;
        }

        .question-number {
          width: 48px;
          height: 48px;
          background: rgb(4, 0, 255);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          font-size: 1.2rem;
          box-shadow: 0 4px 15px rgba(4, 0, 255, 0.4);
        }

        .question-category {
          background: rgba(255, 255, 255, 0.2);
          padding: 8px 16px;
          border-radius: 20px;
          color: white;
          font-size: 0.85rem;
          font-weight: 500;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .question-text {
          color: white;
          font-size: 1.3rem;
          font-weight: 600;
          line-height: 1.5;
          margin-bottom: 24px;
        }

        .options-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 12px;
        }

        .option-card {
          background: rgba(255, 255, 255, 0.1);
          border: 2px solid rgba(255, 255, 255, 0.2);
          border-radius: 16px;
          padding: 20px;
          cursor: pointer;
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
          position: relative;
          overflow: hidden;
        }

        .option-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
          transition: left 0.5s ease;
        }

        .option-card:hover::before {
          left: 100%;
        }

        .option-card:hover {
          transform: translateY(-3px);
          border-color: rgb(4, 0, 255);
          box-shadow: 0 8px 25px rgba(4, 0, 255, 0.3);
        }

        .option-card.selected {
          background: rgba(4, 0, 255, 0.3);
          border-color: rgb(4, 0, 255);
          transform: translateY(-3px);
          box-shadow: 0 8px 25px rgba(4, 0, 255, 0.3);
        }

        .option-content {
          display: flex;
          align-items: center;
          gap: 16px;
          position: relative;
          z-index: 1;
        }

        .option-check {
          width: 24px;
          height: 24px;
          border: 2px solid rgba(255, 255, 255, 0.5);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          transition: all 0.3s ease;
        }

        .option-card.selected .option-check {
          background: white;
          color: rgb(4, 0, 255);
          border-color: white;
        }

        .option-text {
          color: white;
          font-size: 1rem;
          font-weight: 500;
          flex: 1;
        }

        .navigation-buttons {
          display: flex;
          gap: 16px;
          margin-top: 30px;
          justify-content: flex-end;
        }

        .complete-button {
          background: rgb(4, 0, 255);
          box-shadow: 0 4px 15px rgba(4, 0, 255, 0.4);
        }

        .complete-button:hover:not(:disabled) {
          box-shadow: 0 8px 25px rgba(4, 0, 255, 0.5);
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 60px 40px;
          color: white;
        }

        .loading-animation {
          position: relative;
          margin-bottom: 30px;
        }

        .loading-brain {
          color: white;
          z-index: 2;
          position: relative;
          animation: pulse 2s ease-in-out infinite;
        }

        .loading-circles {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }

        .circle {
          position: absolute;
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-top: 3px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .circle-1 {
          width: 80px;
          height: 80px;
          top: -40px;
          left: -40px;
        }

        .circle-2 {
          width: 60px;
          height: 60px;
          top: -30px;
          left: -30px;
          animation-delay: 0.3s;
        }

        .circle-3 {
          width: 100px;
          height: 100px;
          top: -50px;
          left: -50px;
          animation-delay: 0.6s;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .loading-container h2 {
          font-size: 1.8rem;
          font-weight: 700;
          margin-bottom: 12px;
        }

        .results-container {
          text-align: center;
        }

        .results-header {
          margin-bottom: 40px;
        }

        .results-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgb(4, 0, 255);
          padding: 12px 24px;
          border-radius: 25px;
          color: white;
          font-weight: 600;
          margin-bottom: 20px;
          box-shadow: 0 4px 15px rgba(4, 0, 255, 0.4);
        }

        .score-showcase {
          margin: 40px 0;
          position: relative;
        }

        .overall-score {
          position: relative;
          display: inline-block;
        }

        .score-circle {
          width: 180px;
          height: 180px;
          background: rgb(4, 0, 255);
          border-radius: 50%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: white;
          box-shadow: 0 20px 40px rgba(4, 0, 255, 0.4);
          position: relative;
          z-index: 2;
          animation: scoreReveal 1s ease-out;
        }

        .score-value {
          font-size: 3rem;
          font-weight: 800;
          line-height: 1;
        }

        .score-label {
          font-size: 0.9rem;
          font-weight: 500;
          opacity: 0.9;
          margin-top: 4px;
        }

        .score-gradient {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 220px;
          height: 220px;
          background: rgb(4, 0, 255);
          border-radius: 50%;
          filter: blur(20px);
          opacity: 0.3;
          z-index: 1;
          animation: pulseGlow 3s ease-in-out infinite;
        }

        @keyframes scoreReveal {
          0% {
            transform: scale(0) rotate(-180deg);
            opacity: 0;
          }
          100% {
            transform: scale(1) rotate(0deg);
            opacity: 1;
          }
        }

        @keyframes pulseGlow {
          0%, 100% { transform: translate(-50%, -50%) scale(1); }
          50% { transform: translate(-50%, -50%) scale(1.1); }
        }

        .metrics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 24px;
          margin: 40px 0;
        }

        .metric-card {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 24px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          transition: transform 0.3s ease;
        }

        .metric-card:hover {
          transform: translateY(-5px);
        }

        .metric-card.critical {
          border-left: 4px solid rgb(4, 0, 255);
        }

        .metric-card.situational {
          border-left: 4px solid rgb(4, 0, 255);
        }

        .metric-card.analytical {
          border-left: 4px solid rgb(4, 0, 255);
        }

        .metric-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 20px;
          color: white;
        }

        .metric-header span {
          font-weight: 600;
          font-size: 0.95rem;
        }

        .metric-chart {
          position: relative;
          width: 120px;
          height: 120px;
          margin: 0 auto;
        }

        .chart-overlay {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          text-align: center;
        }

        .chart-score {
          color: white;
          font-size: 1.5rem;
          font-weight: 700;
        }

        .comparison-section {
          margin: 30px 0;
          text-align: center;
        }

        .comparison-title {
          color: white;
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 24px;
        }

        .comparison-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 16px;
          margin-bottom: 16px;
        }

        .comparison-card {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 20px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(20px);
          transition: all 0.3s ease;
          animation: cardFadeIn 0.6s ease-out;
          animation-fill-mode: backwards;
        }

        .comparison-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
        }

        .comparison-card.world {
          border-left: 4px solid rgb(4, 0, 255);
          background: linear-gradient(135deg, rgba(4, 0, 255, 0.1), rgba(255, 255, 255, 0.05));
        }

        .comparison-card.country {
          border-left: 4px solid #22c55e;
          background: linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(255, 255, 255, 0.05));
        }

        .comparison-card.state {
          border-left: 4px solid #a855f7;
          background: linear-gradient(135deg, rgba(168, 85, 247, 0.1), rgba(255, 255, 255, 0.05));
        }

        .comparison-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
          color: white;
          font-size: 1rem;
          font-weight: 600;
        }

        .comparison-body p {
          color: white;
          font-size: 1.1rem;
          font-weight: 500;
          margin-bottom: 12px;
        }

        .top-badge {
          background: rgb(4, 0, 255);
          color: white;
          padding: 6px 12px;
          border-radius: 12px;
          font-size: 0.85rem;
          font-weight: 600;
          display: inline-block;
        }

        .comparison-card.country .top-badge {
          background: #22c55e;
        }

        .comparison-card.state .top-badge {
          background: #a855f7;
        }

        .comparison-note {
          color: rgba(255, 255, 255, 0.8);
          font-size: 0.9rem;
          margin-top: 12px;
        }

        .join-program {
          margin: 40px 0;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .join-image {
          max-width: 100%;
          width: 300px;
          height: auto;
          border-radius: 16px;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
          transition: transform 0.3s ease;
          cursor: pointer;
          margin: 0;
        }

        .join-image:hover {
          transform: scale(1.05);
        }

        @media (max-width: 480px) {
          .join-image {
            width: 250px;
          }
        }

        @keyframes cardFadeIn {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .action-buttons {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
          margin-top: 40px;
        }

        @keyframes slideUp {
          0% {
            transform: translateY(50px);
            opacity: 0;
          }
          100% {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .fade-in {
          animation: fadeIn 0.6s ease-out;
        }

        @keyframes fadeIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }

        @media (max-width: 768px) {
          .main-container {
            padding: 16px;
          }

          .card {
            padding: 24px;
          }

          .metrics-grid {
            grid-template-columns: 1fr;
          }

          .comparison-grid {
            grid-template-columns: 1fr;
          }

          .action-buttons {
            flex-direction: column;
          }

          .navigation-buttons {
            flex-direction: column;
            justify-content: center;
          }

          .score-circle {
            width: 150px;
            height: 150px;
          }

          .score-value {
            font-size: 2.5rem;
          }
        }

        @media (max-width: 480px) {
          .options-grid {
            gap: 8px;
          }

          .option-card {
            padding: 16px;
          }

          .question-text {
            font-size: 1.1rem;
          }

          .comparison-body p {
            font-size: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default Psychology;